/**
 * Integrytas Group - Framework Dependency Map & Breaking-Change Analyzer (FDM)
 * Page: FDM.MAP
 *
 * Browse the configuration dependency graph, inspect a node's direct
 * dependencies/dependents, and run a transitive impact analysis before changing
 * or deleting a system parameter, column, report, business event, or import map.
 * Edges are parsed automatically when definitions are saved; "Reparse" rebuilds
 * the whole graph from current definitions.
 */

import { Fragment, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid } from '../../components/personalization';
import type { GridColumnDef } from '../../components/personalization';
import { apiDb } from '../../lib/api_client';
import { EmptyState } from '../../components/common/EmptyState';
import { ErrorBanner } from '../../components/common/ErrorBanner';
import type { AuthSession } from '../../types/auth';

interface NodeRow { fdm_node_id: number; node_type: string; node_key: string; label: string | null; source_table: string | null; source_pk: string | null }
interface GraphRef { node_type: string; node_key: string; label: string | null; relationship: string }
interface NodeGraph { node: NodeRow | null; dependencies: GraphRef[]; dependents: GraphRef[] }
interface ImpactRow { node_type: string; node_key: string; label: string | null; relationship: string; depth: number; direct: boolean }

export interface FdmDependencyMapPageProps { session: AuthSession }

export function FdmDependencyMapPage(props: FdmDependencyMapPageProps) {
  return (
    <PersonalizationProvider pageCode="FDM.MAP" session={props.session}>
      <FdmDependencyMapPageInner {...props} />
    </PersonalizationProvider>
  );
}

function FdmDependencyMapPageInner({ session }: FdmDependencyMapPageProps) {
  const { t } = useTranslation();
  const TYPE_LABEL: Record<string, string> = {
    SYSTEM_PARAMETER: t('fdm.type.system_parameter', 'System Parameter'),
    TABLE: t('fdm.type.table', 'Table'),
    TABLE_COLUMN: t('fdm.type.column', 'Column'),
    DOC_TEMPLATE: t('fdm.type.report', 'Report'),
    ECM_TEMPLATE: t('fdm.type.comm_template', 'Comm Template'),
    MTA_RULE: t('fdm.type.business_event', 'Business Event'),
    UPP_DEFINITION: t('fdm.type.import_export', 'Import/Export'),
    PAGE: t('fdm.type.page', 'Page'),
  };
  const [nodes, setNodes] = useState<NodeRow[]>([]);
  const [typeFilter, setTypeFilter] = useState('');
  const [search, setSearch] = useState('');
  const [sel, setSel] = useState<NodeRow | null>(null);
  const [graph, setGraph] = useState<NodeGraph | null>(null);
  const [impact, setImpact] = useState<ImpactRow[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // §17.B13 related-children disclosure for the impact matrix. Each impact row is an
  // affected artifact with no further child collection loaded here, so the control
  // renders inert (keyed by row index).
  const detail = useExpandedRows<number>();
  const buildRelatedGroups = (_i: number): RelatedChildGroup[] => []; // TODO: wire relations
  const hasRelated = (i: number) => buildRelatedGroups(i).some(g => g.items.length > 0);

  const reload = async () => {
    setBusy(true); setError(null);
    try {
      const { data, error: e } = await apiDb.from('fdm_node').select('fdm_node_id, node_type, node_key, label, source_table, source_pk').order('node_type').order('node_key').limit(1000);
      if (e) throw e;
      setNodes((data ?? []) as NodeRow[]);
    } catch (err) { setError((err as Error).message); } finally { setBusy(false); }
  };
  useEffect(() => { void reload(); }, []);

  const selectNode = async (n: NodeRow) => {
    setSel(n); setGraph(null); setImpact(null); setError(null); setBusy(true);
    try {
      const { data, error: e } = await apiDb.rpc<NodeGraph>('fdm_node_graph', { p_node_type: n.node_type, p_node_key: n.node_key });
      if (e) throw e;
      setGraph(data ?? null);
    } catch (err) { setError((err as Error).message); } finally { setBusy(false); }
  };

  const runImpact = async () => {
    if (!sel) return;
    setBusy(true); setError(null);
    try {
      const { data, error: e } = await apiDb.rpc<ImpactRow[]>('fdm_impact_for', { p_node_type: sel.node_type, p_node_key: sel.node_key });
      if (e) throw e;
      setImpact(Array.isArray(data) ? data : []);
    } catch (err) { setError((err as Error).message); } finally { setBusy(false); }
  };

  const reparse = async () => {
    setBusy(true); setError(null);
    try {
      const { data, error: e } = await apiDb.rpc<{ ok: boolean; error_code?: string }>('fdm_reextract_all');
      if (e) throw e;
      if (data && data.ok === false) setError(data.error_code ?? t('fdm.reparse_refused', 'Reparse refused.'));
      await reload();
    } catch (err) { setError((err as Error).message); } finally { setBusy(false); }
  };

  const types = useMemo(() => Array.from(new Set(nodes.map(n => n.node_type))).sort(), [nodes]);
  const filtered = useMemo(() => nodes.filter(n =>
    (!typeFilter || n.node_type === typeFilter) &&
    (!search || n.node_key.toLowerCase().includes(search.toLowerCase()) || (n.label ?? '').toLowerCase().includes(search.toLowerCase()))
  ), [nodes, typeFilter, search]);

  const IMPACT_COLS: GridColumnDef[] = [
    { key: 'type',         header: t('fdm.col.type', 'Type'),                 defaultWidth: 140 },
    { key: 'artifact',     header: t('fdm.col.artifact', 'Artifact'),         defaultWidth: 220 },
    { key: 'relationship', header: t('fdm.col.relationship', 'Relationship'), defaultWidth: 140 },
    { key: 'depth',        header: t('fdm.col.depth', 'Depth'),               defaultWidth: 100 },
  ];

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-10 shrink-0 items-center gap-2 border-b border-csm-border bg-white px-3">
        <span className="csm-page-title">{t('fdm.title', 'Dependency Map')}</span>
        <button onClick={() => void reload()} className="h-7 rounded border border-csm-border bg-white px-2 text-csm-sm hover:bg-csm-surface-alt">{t('fdm.refresh', 'Refresh')}</button>
        <button onClick={() => void reparse()} className="h-7 rounded border border-csm-border bg-white px-2 text-csm-sm hover:bg-csm-surface-alt">{t('fdm.reparse', 'Reparse')}</button>
        {busy && <span className="text-csm-xs text-csm-watermark">{t('fdm.working', 'Working…')}</span>}
        <div className="ml-auto"><PersonalizationToggle /></div>
      </div>
      {error && (
        <ErrorBanner
          message={t('fdm.error_banner', "Couldn't complete the last dependency-map operation.")}
          detail={error}
          retry_label={t('common.retry', 'Retry')}
          on_retry={() => void reload()}
          details_label={t('common.details', 'Details')}
        />
      )}

      <div className="flex min-h-0 flex-1">
        {/* Node browser */}
        <div className="flex w-80 shrink-0 flex-col border-r border-csm-border">
          <div className="flex items-center gap-1 border-b border-csm-border-soft p-2">
            <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} className="h-7 rounded border border-csm-border px-1 text-csm-xs">
              <option value="">{t('fdm.all_types', 'All types')}</option>
              {types.map(ty => <option key={ty} value={ty}>{TYPE_LABEL[ty] ?? ty}</option>)}
            </select>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder={t('fdm.filter_ph', 'Filter…')} className="h-7 flex-1 rounded border border-csm-border px-1 text-csm-xs" />
          </div>
          <div className="min-h-0 flex-1 overflow-auto">
            {filtered.map(n => (
              <button key={n.fdm_node_id} onClick={() => void selectNode(n)}
                className={`flex w-full flex-col items-start border-b border-csm-border-soft px-2 py-1 text-left hover:bg-csm-surface-alt ${sel?.fdm_node_id === n.fdm_node_id ? 'bg-csm-surface-alt' : ''}`}>
                <span className="font-mono text-csm-xs text-csm-ink">{n.node_key}</span>
                <span className="text-csm-2xs uppercase tracking-wide text-csm-watermark">{TYPE_LABEL[n.node_type] ?? n.node_type}</span>
              </button>
            ))}
            {filtered.length === 0 && <div className="p-3 text-csm-xs text-csm-watermark">{t('fdm.no_nodes', 'No nodes. Save a report/business-event/import definition, or click Reparse.')}</div>}
          </div>
        </div>

        {/* Detail */}
        <div className="min-h-0 flex-1 overflow-auto p-3">
          {!sel && <div className="text-csm-sm text-csm-watermark">{t('fdm.select_node', 'Select a node to inspect its dependencies and run an impact analysis.')}</div>}
          {sel && (
            <>
              <div className="mb-3 flex items-center gap-2">
                <div>
                  <div className="font-mono text-csm-base text-csm-ink">{sel.node_key}</div>
                  <div className="text-csm-2xs uppercase tracking-wide text-csm-watermark">{TYPE_LABEL[sel.node_type] ?? sel.node_type}{sel.label && sel.label !== sel.node_key ? ` · ${sel.label}` : ''}</div>
                </div>
                <button onClick={() => void runImpact()} className="ml-auto h-7 rounded border border-primary bg-primary px-3 text-csm-sm text-white hover:opacity-90">{t('fdm.run_impact', 'Run Impact Analysis')}</button>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <RefList title={t('fdm.depends_on', 'Depends on (this node uses)')} refs={graph?.dependencies ?? []} onPick={pick => { const f = nodes.find(n => n.node_type === pick.node_type && n.node_key === pick.node_key); if (f) void selectNode(f); }} />
                <RefList title={t('fdm.dependents', 'Dependents (would break)')} refs={graph?.dependents ?? []} onPick={pick => { const f = nodes.find(n => n.node_type === pick.node_type && n.node_key === pick.node_key); if (f) void selectNode(f); }} />
              </div>

              {impact && (
                <div className="mt-4">
                  <h4 className="mb-1 text-csm-sm font-semibold text-csm-ink">{t('fdm.impact_matrix', 'Impact Matrix — {{count}} affected artifact(s)', { count: impact.length })} {impact.length > 0 && <span className="text-csm-danger">{t('fdm.breaking_change', '· deleting/deprecating this is a breaking change')}</span>}</h4>
                  <PersonalizedGrid componentKey="impact" columns={IMPACT_COLS}>
                    {(cols, widths) => (
                      <table className="w-full border-collapse text-csm-sm">
                        <thead><tr className="border-b border-csm-border text-left text-csm-2xs uppercase tracking-wide text-csm-watermark">
                          <th className="w-7 px-2 py-1" title={t('fdm.related.header', 'Related records')} />
                          {cols.map(c => <th key={c.key} className="px-2 py-1" style={{ width: widths[c.key] }}>{c.header}</th>)}
                        </tr></thead>
                        <tbody>
                          {impact.map((r, i) => (
                            <Fragment key={`${r.node_type}:${r.node_key}:${i}`}>
                            <tr className="border-b border-csm-border-soft">
                              <td className="px-1 py-1 text-center align-middle" onClick={e => e.stopPropagation()}>
                                <RelatedExpandButton open={detail.isOpen(i)} hasChildren={hasRelated(i)} onToggle={() => detail.toggle(i)} expandLabel={t('fdm.related.expand', 'Show related records')} collapseLabel={t('fdm.related.collapse', 'Hide related')} tooltip={t('fdm.related.tooltip', 'Show related records')} />
                              </td>
                              {cols.map(c => {
                                if (c.key === 'type')         return <td key={c.key} style={{ width: widths[c.key] }} className="px-2 py-1 text-csm-xs text-csm-ink-soft">{TYPE_LABEL[r.node_type] ?? r.node_type}</td>;
                                if (c.key === 'artifact')     return <td key={c.key} style={{ width: widths[c.key] }} className="px-2 py-1 font-mono text-csm-xs">{r.node_key}</td>;
                                if (c.key === 'relationship') return <td key={c.key} style={{ width: widths[c.key] }} className="px-2 py-1 text-csm-2xs uppercase tracking-wide text-csm-watermark">{r.relationship}</td>;
                                if (c.key === 'depth')        return <td key={c.key} style={{ width: widths[c.key] }} className="px-2 py-1 text-csm-xs">{r.direct ? <span className="text-csm-danger">{t('fdm.direct', 'direct')}</span> : t('fdm.hops', '{{depth}} hops', { depth: r.depth })}</td>;
                                return null;
                              })}
                            </tr>
                            {detail.isOpen(i) && (
                              <tr>
                                <td colSpan={cols.length + 1} className="border-b border-csm-border bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}>
                                  <RelatedChildrenPanel groups={buildRelatedGroups(i)} session={session} />
                                </td>
                              </tr>
                            )}
                            </Fragment>
                          ))}
                          {impact.length === 0 && <tr><td colSpan={cols.length + 1} className="px-2 py-6 text-center">
                            <EmptyState variant="all-clear" title={t('fdm.no_dependents', 'No dependents — safe to change or delete.')} />
                          </td></tr>}
                        </tbody>
                      </table>
                    )}
                  </PersonalizedGrid>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function RefList({ title, refs, onPick }: { title: string; refs: GraphRef[]; onPick: (r: GraphRef) => void }): ReactNode {
  const { t } = useTranslation();
  return (
    <div className="rounded border border-csm-border-soft">
      <div className="border-b border-csm-border-soft bg-csm-surface-alt px-2 py-1 text-csm-2xs font-semibold uppercase tracking-wide text-csm-ink-soft">{title}</div>
      <div className="max-h-60 overflow-auto">
        {refs.map((r, i) => (
          <button key={`${r.node_type}:${r.node_key}:${i}`} onClick={() => onPick(r)} className="flex w-full items-center gap-2 border-b border-csm-border-soft px-2 py-1 text-left hover:bg-csm-surface-alt">
            <span className="font-mono text-csm-xs text-csm-ink">{r.node_key}</span>
            <span className="ml-auto text-csm-2xs uppercase tracking-wide text-csm-watermark">{r.relationship}</span>
          </button>
        ))}
        {refs.length === 0 && <div className="px-2 py-2 text-csm-xs text-csm-watermark">{t('fdm.none', 'None.')}</div>}
      </div>
    </div>
  );
}
